import React, {
  FunctionComponent,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { HeaderTestIds } from '@trello/webdriverio-tests';
import { IconName } from 'app/common/components/controls/icon';
import { MemberModel } from 'app/gamma/src/types/models';
import HeaderButton from './button';
import { Analytics } from '@trello/atlassian-analytics';
import { trackUe, Noun, Verb } from '@trello/analytics';
import { useFeatureFlag, featureFlagClient } from '@trello/feature-flag-client';
import { matches } from '@trello/locale';
import {
  AtlassianSwitcherPrefetchTrigger,
  defaultJoinableSitesFetch,
  createJoinableSitesProvider,
  createAvailableProductsProvider,
} from '@atlaskit/atlassian-switcher';
import { atlassianApiGateway, siteDomain } from '@trello/config';
import { AtlassianSwitcherVariations } from 'app/src/components/AtlassianAppSwitcher/AtlassianAppSwitcher';
import {
  OverlayType,
  setOverlay,
} from 'app/gamma/src/modules/state/ui/overlay';
import {
  setLocationToWacSoftware,
  getCrossFlowEssentialsV2Props,
  AVAILABLE_PRODUCTS_WORKLENS_URL,
  AVAILABLE_SITES_PROXY_URL,
} from 'app/gamma/src/util/cross-flow-essentials';
import { usePopover, Popover } from '@trello/nachos';
import { AtlassianAppSwitcher } from 'app/src/components/AtlassianAppSwitcher';
import { useDispatch } from 'react-redux';
import { ProductKeys } from '@atlassiansox/product-store-react';
import { Editions } from '@atlassiansox/cross-flow-react';
import { forTemplate } from 'app/common/lib/util/i18n';
import { FeatureFlagClient } from '@trello/feature-flag-client/src/feature-flag-client';
import { isDesktop } from '@trello/browser';

interface AtlassianAppSwitcherButtonProps {
  member: MemberModel | undefined;
}

const format = forTemplate('cross_flow');

const joinableSitesDataProviderForAa = createJoinableSitesProvider(
  defaultJoinableSitesFetch(`${atlassianApiGateway}gateway/api/growth`),
);
const joinableSitesDataProviderForNonAa = createJoinableSitesProvider(
  defaultJoinableSitesFetch(`${siteDomain}/proxy/experiment-api`),
);

const availableSitesDataProviderForAa = createAvailableProductsProvider(
  AVAILABLE_PRODUCTS_WORKLENS_URL,
);
const availableSitesDataProviderForNonAa = createAvailableProductsProvider(
  AVAILABLE_SITES_PROXY_URL,
);

export const AtlassianAppSwitcherButton: FunctionComponent<AtlassianAppSwitcherButtonProps> = ({
  member,
}) => {
  const locale = member?.prefs?.locale;
  const aaId = member?.aaId || undefined;
  const isAaMastered = member?.isAaMastered;
  const isNonAaEmailVerified = !member?.isAaMastered && member?.confirmed;
  const defaultSignupEmail = member?.email;

  const switcherVariation = useFeatureFlag(
    'a4aa.atlassian-app-switcher',
    AtlassianSwitcherVariations.NOT_ENROLLED,
  );
  const isNonAaJoinableSitesEnabled = useFeatureFlag(
    'aa4a.atlassian-switcher-joinable-sites-v2',
    false,
  );
  const crossflowFeatureFlag = useFeatureFlag(
    'growth.cross-flow-essentials-v2-atlassian-switcher',
    'not-enrolled',
  );
  const areAllLocalesEnabled = useFeatureFlag(
    'growth.cross-flow-essentials-v2-atlassian-switcher-all-locales',
    false,
  );
  const { isCrossFlowV2Enabled, edition } = getCrossFlowEssentialsV2Props(
    crossflowFeatureFlag,
  );
  const isNonAaFlagEnabled = useFeatureFlag(
    'growth.cross-flow-essentials-v2-atlassian-switcher-non-aa',
    false,
  );
  const isNonAaEnabled =
    isNonAaEmailVerified &&
    isNonAaFlagEnabled &&
    (isCrossFlowV2Enabled || isNonAaJoinableSitesEnabled);

  let joinableSitesDataProvider = undefined;
  let availableProductsDataProvider = undefined;
  if (isAaMastered) {
    joinableSitesDataProvider = joinableSitesDataProviderForAa;
    availableProductsDataProvider = availableSitesDataProviderForAa;
  } else if (isNonAaEmailVerified && isNonAaFlagEnabled) {
    availableProductsDataProvider = availableSitesDataProviderForNonAa;
    if (isNonAaJoinableSitesEnabled) {
      joinableSitesDataProvider = joinableSitesDataProviderForNonAa;
    }
  }

  const isEnabledForLocale =
    areAllLocalesEnabled ||
    (typeof locale === 'string' && matches('en', locale));

  const [showAppSwitcher, setShowAppSwitcher] = useState(false);

  const onShow = useCallback(() => {
    Analytics.sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'atlassianSwitcherHeaderButton',
      source: 'atlassianSwitcherHeaderButton',
      attributes: {
        aaId,
        isAaMastered,
      },
    });
    trackUe({
      category: Noun.ATLASSIAN_APP_SWITCHER,
      verb: Verb.CLICKS,
      directObj: Noun.ATLASSIAN_APP_SWITCHER_BUTTON,
    });

    if (isCrossFlowV2Enabled) {
      featureFlagClient.getTrackedVariation(
        'growth.cross-flow-essentials-v2-atlassian-switcher',
        'not-enrolled',
      );
    }
    if (isNonAaEnabled && isNonAaJoinableSitesEnabled) {
      featureFlagClient.getTrackedVariation(
        'aa4a.atlassian-switcher-joinable-sites-v2',
        false,
      );
    }
  }, [
    aaId,
    isAaMastered,
    isCrossFlowV2Enabled,
    isNonAaEnabled,
    isNonAaJoinableSitesEnabled,
  ]);

  useEffect(() => {
    const checkShowAppSwitcher = async () => {
      // If the user isn't enrolled, or your locale is not enrolled, do nothing.
      if (
        switcherVariation === AtlassianSwitcherVariations.NOT_ENROLLED ||
        !isEnabledForLocale ||
        isAaMastered === undefined || //  if this field has not been loaded yet (partial /me data is pulled from fetching members)
        isDesktop() // If this is the desktop version, do nothing.
      ) {
        return;
      }

      featureFlagClient.getTrackedVariation(
        'growth.cross-flow-essentials-v2-atlassian-switcher-all-locales',
        false,
      );

      if (isAaMastered || (isNonAaEmailVerified && isNonAaFlagEnabled)) {
        const attributes = { aaId, isAaMastered };

        featureFlagClient.getTrackedVariation(
          'a4aa.atlassian-app-switcher',
          AtlassianSwitcherVariations.NOT_ENROLLED,
          attributes,
        );

        if (isCrossFlowV2Enabled) {
          if (switcherVariation === AtlassianSwitcherVariations.CONTROL) {
            FeatureFlagClient.triggerExposureEvent(
              'growth.cross-flow-essentials-v2-atlassian-switcher',
              AtlassianSwitcherVariations.CONTROL,
              'TARGET_MATCHED',
              undefined,
              attributes,
            );
          } else {
            featureFlagClient.getTrackedVariation(
              'growth.cross-flow-essentials-v2-atlassian-switcher',
              AtlassianSwitcherVariations.NOT_ENROLLED,
              attributes,
            );
          }
        }

        if (switcherVariation === AtlassianSwitcherVariations.EXPERIMENT) {
          setShowAppSwitcher(true);
        }
      }
    };

    checkShowAppSwitcher();
  }, [
    switcherVariation,
    isAaMastered,
    isNonAaEmailVerified,
    isNonAaFlagEnabled,
    isEnabledForLocale,
    isCrossFlowV2Enabled,
    aaId,
  ]);

  const { popoverProps, triggerRef, toggle, hide } = usePopover<
    HTMLButtonElement
  >({
    onShow,
  });

  const dispatch = useDispatch();

  const handleTryClick = useCallback(
    (productKey: ProductKeys) => {
      hide();
      dispatch(
        setOverlay({
          overlayType: OverlayType.ProductStore,
          productKey,
          context: {
            source: 'try',
          },
        }),
      );
    },
    [dispatch, hide],
  );

  const isFreeEditionCrossFlow = edition === Editions.FREE;

  const handleDiscoverMoreClick = useCallback(() => {
    hide();

    if (isCrossFlowV2Enabled) {
      dispatch(
        setOverlay({
          overlayType: OverlayType.ProductStore,
          context: {
            source: 'discover-more',
          },
        }),
      );
    } else {
      setLocationToWacSoftware({ campaign: 'product_store' });
    }
  }, [hide, isCrossFlowV2Enabled, dispatch]);

  return showAppSwitcher ? (
    <>
      <AtlassianSwitcherPrefetchTrigger
        joinableSitesDataProvider={joinableSitesDataProvider}
        availableProductsDataProvider={availableProductsDataProvider}
      >
        <HeaderButton
          icon={IconName.AppSwitcher}
          onClick={toggle}
          ref={triggerRef}
          testId={HeaderTestIds.AtlassianAppSwitcher}
        />
      </AtlassianSwitcherPrefetchTrigger>
      <Popover
        {...popoverProps}
        title={format('more from atlassian')}
        testId={HeaderTestIds.AtlassianAppSwitcher}
      >
        <AtlassianAppSwitcher
          aaId={aaId}
          triggerXFlow={handleTryClick}
          onDiscoverMoreClicked={handleDiscoverMoreClick}
          joinableSitesDataProvider={joinableSitesDataProvider}
          availableProductsDataProvider={availableProductsDataProvider}
          isDefaultEditionFreeExperimentEnabled={isFreeEditionCrossFlow}
          nonAaMastered={!isAaMastered}
          defaultSignupEmail={defaultSignupEmail}
          onClose={hide}
          locale={locale}
        />
      </Popover>
    </>
  ) : null;
};
